import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { ArrowLeft, Plus, Trash2, Upload, Loader2, ChevronDown, RotateCcw, Star, Clock, DollarSign, Sparkles, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CATEGORY_LIST } from '@/constants/repairOptions';

const UNITS = ['service', 'each', 'hour', 'axle', 'per_tire', 'flat', 'per_mile', 'per_day'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const fmt = (n) => n != null ? new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(n) : '—';

export default function VendorPricing() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showOpsProfile, setShowOpsProfile] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [catFilter, setCatFilter] = useState('');
  const [saving, setSaving] = useState(false);

  // Import state
  const [importPreview, setImportPreview] = useState(null);
  const [importMapping, setImportMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  const aiFileRef = useRef(null);
  const [aiExtracting, setAiExtracting] = useState(false);

  // Forms
  const [itemForm, setItemForm] = useState({ category:'', service_code:'', description:'', unit_price:'', unit:'service', effective_from:'', notes:'' });
  const [opsForm, setOpsForm] = useState({ coverage_states:[], categories:[], ops_rating:'', eta_hours:'', travel_fee_per_mile:'', contract_notes:'' });

  useEffect(() => { load(); }, [vendorId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/vendor-pricing/vendors/${vendorId}/pricing`);
      setData(res.data);
      const v = res.data.vendor || {};
      setOpsForm({
        coverage_states: v.coverage_states || [],
        categories: v.categories || [],
        ops_rating: v.ops_rating || '',
        eta_hours: v.eta_hours || '',
        travel_fee_per_mile: v.travel_fee_per_mile || '',
        contract_notes: v.contract_notes || '',
      });
    } catch { toast.error('Failed to load pricing'); }
    finally { setLoading(false); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/vendor-pricing/vendors/${vendorId}/pricing/items`, { ...itemForm, unit_price: Number(itemForm.unit_price) });
      toast.success('Price item added');
      setShowAdd(false);
      setItemForm({ category:'', service_code:'', description:'', unit_price:'', unit:'service', effective_from:'', notes:'' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/vendor-pricing/vendors/${vendorId}/pricing/items/${editItem.id}`, editItem);
      toast.success('Updated');
      setEditItem(null);
      load();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const deleteItem = async (itemId) => {
    await api.delete(`/vendor-pricing/vendors/${vendorId}/pricing/items/${itemId}`);
    toast.success('Removed');
    load();
  };

  const saveOpsProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/vendor-pricing/vendors/${vendorId}/ops-profile`, opsForm);
      toast.success('Ops profile updated');
      setShowOpsProfile(false);
      load();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await api.post(`/vendor-pricing/vendors/${vendorId}/pricing/import/preview`, fd);
      setImportPreview(res.data);
      setImportMapping(res.data.auto_mapping || {});
      toast.success(`Parsed ${res.data.total_rows} rows`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Parse failed'); }
  };

  // Option C — AI extract from PDF/image price sheets. Uses the same GPT-4o
  // pipeline as the invoice extractor, but with a price-sheet-specific prompt.
  // Returns rows in the same shape as CSV preview, so confirmImport works as-is.
  const handleAiExtractFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAiExtracting(true);
    setImportPreview(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await api.post(`/vendor-pricing/vendors/${vendorId}/pricing/import/ai-extract`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview(res.data);
      setImportMapping(res.data.auto_mapping || {});
      toast.success(`AI extracted ${res.data.total_rows} priced rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI extraction failed — try CSV/Excel instead');
    } finally {
      setAiExtracting(false);
      if (aiFileRef.current) aiFileRef.current.value = '';
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const res = await api.post(`/vendor-pricing/vendors/${vendorId}/pricing/import/confirm`, {
        rows: importPreview.preview.slice(0, 500),
        mapping: importMapping,
        filename: importPreview.filename || 'import'
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.created} price items!`);
      load();
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  const activateVersion = async (vId) => {
    await api.post(`/vendor-pricing/vendors/${vendorId}/pricing/versions/${vId}/activate`);
    toast.success('Version activated');
    load();
  };

  const filteredItems = (data?.items || []).filter(i => !catFilter || i.category === catFilter);
  const vendor = data?.vendor || {};

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="text-[#3B82F6] animate-spin" size={28} /></div>;

  return (
    <div className="p-6 space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b border-[#2D3F5E]/60">
        <button onClick={() => navigate('/ops/vendors')} className="text-[#5C6A7A] hover:text-[#3B82F6] transition-colors"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-0.5">// VENDOR PRICING</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">{vendor.name}</h1>
          <div className="flex items-center gap-4 mt-1">
            {vendor.city && <span className="text-[#5C6A7A] text-xs font-['IBM_Plex_Sans']">{vendor.city}, {vendor.us_state}</span>}
            {vendor.ops_rating > 0 && (
              <span className="flex items-center gap-1 text-[#3B82F6] text-xs font-['JetBrains_Mono']">
                <Star size={11} /> {vendor.ops_rating}/5
              </span>
            )}
            {vendor.eta_hours && <span className="flex items-center gap-1 text-[#5C6A7A] text-xs font-['JetBrains_Mono']"><Clock size={11} /> {vendor.eta_hours}h ETA</span>}
            {(data?.items || []).length > 0 && <span className="text-[9px] font-['JetBrains_Mono'] text-[#57EB31] uppercase">{(data?.items || []).length} price items</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowOpsProfile(true)} className="px-3 py-2 border border-[#2D3F5E] text-[#5C6A7A] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
            Ops Profile
          </button>
          <button onClick={() => { setShowImport(true); setImportPreview(null); setImportResult(null); }}
            className="flex items-center gap-2 px-3 py-2 border border-[#2D3F5E] text-[#B8C0CC] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['IBM_Plex_Sans']">
            <Upload size={13} /> Import Sheets
          </button>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors">
            <Plus size={14} /> Add Price
          </button>
        </div>
      </div>

      {/* Version history strip */}
      {(data?.versions || []).length > 0 && (
        <div className="flex items-center gap-3 overflow-x-auto py-1">
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] flex-shrink-0">Versions:</p>
          {data.versions.map(v => (
            <div key={v.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border flex-shrink-0 text-xs font-['JetBrains_Mono'] ${v.status === 'ACTIVE' ? 'border-[#57EB31]/40 bg-[#57EB31]/8 text-[#57EB31]' : 'border-[#2D3F5E] text-[#5C6A7A]'}`}>
              <span>{v.label || v.id.slice(0,8)}</span>
              <span className="opacity-60">{v.item_count} items</span>
              {v.status === 'ACTIVE' ? <span className="text-[9px] uppercase">ACTIVE</span> : (
                <button onClick={() => activateVersion(v.id)} className="hover:text-[#3B82F6]"><RotateCcw size={10} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Category filter */}
      <div className="flex items-center gap-3">
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="h-8 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]">
          <option value="">All Categories</option>
          {CATEGORY_LIST.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <span className="text-[#5C6A7A] text-xs font-['JetBrains_Mono']">{filteredItems.length} items</span>
      </div>

      {/* Pricing Table */}
      <div className="card-industrial overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#2D3F5E]">
              {['CATEGORY','CODE','DESCRIPTION','UNIT','PRICE','EFF. DATE','NOTES',''].map(h => (
                <th key={h} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-16 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">
                <DollarSign size={28} className="mx-auto mb-3 text-[#2D3F5E]" />
                No pricing set. Add manually or import from Sheets.
              </td></tr>
            ) : filteredItems.map((item, i) => (
              <tr key={item.id} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                style={{ background: i%2!==0?'rgba(255,255,255,0.01)':'transparent' }}>
                <td className="px-4 py-2.5 text-xs font-['JetBrains_Mono'] text-[#3B82F6]">{item.category}</td>
                <td className="px-4 py-2.5 text-[10px] font-['JetBrains_Mono'] text-[#5C6A7A]">{item.service_code || '—'}</td>
                <td className="px-4 py-2.5 text-sm text-white font-['IBM_Plex_Sans']">{item.description}</td>
                <td className="px-4 py-2.5 text-xs text-[#5C6A7A] font-['JetBrains_Mono']">{item.unit}</td>
                <td className="px-4 py-2.5 font-['JetBrains_Mono'] font-bold text-[#E8EAED]">{fmt(item.unit_price)}</td>
                <td className="px-4 py-2.5 text-xs text-[#5C6A7A] font-['JetBrains_Mono']">{item.effective_from || '—'}</td>
                <td className="px-4 py-2.5 text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] max-w-[120px] truncate">{item.notes || '—'}</td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-2">
                    <button onClick={() => setEditItem({...item})} className="text-[#5C6A7A] hover:text-[#3B82F6] text-[10px] font-['JetBrains_Mono']">EDIT</button>
                    <button onClick={() => deleteItem(item.id)} className="text-[#2D3F5E] hover:text-red-400 transition-colors"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Item Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-lg">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">Add Price Item</DialogTitle></DialogHeader>
          <form onSubmit={addItem} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Category *</label>
                <select value={itemForm.category} onChange={e => setItemForm(p=>({...p,category:e.target.value}))} required
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]">
                  <option value="">Select...</option>
                  {CATEGORY_LIST.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Service Code</label>
                <input type="text" value={itemForm.service_code} onChange={e => setItemForm(p=>({...p,service_code:e.target.value}))} placeholder="e.g. OIL_CHANGE"
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div className="col-span-2">
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Description *</label>
                <input type="text" required value={itemForm.description} onChange={e => setItemForm(p=>({...p,description:e.target.value}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Unit Price ($) *</label>
                <input type="number" step="0.01" required value={itemForm.unit_price} onChange={e => setItemForm(p=>({...p,unit_price:e.target.value}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Unit</label>
                <select value={itemForm.unit} onChange={e => setItemForm(p=>({...p,unit:e.target.value}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]">
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Effective From</label>
                <input type="date" value={itemForm.effective_from} onChange={e => setItemForm(p=>({...p,effective_from:e.target.value}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Notes</label>
                <input type="text" value={itemForm.notes} onChange={e => setItemForm(p=>({...p,notes:e.target.value}))} placeholder="incl. mounting..."
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
              </div>
            </div>
            <button type="submit" disabled={saving} className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />} Add Price Item
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      {editItem && (
        <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
          <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-lg">
            <DialogHeader><DialogTitle className="text-white font-['Manrope']">Edit Price Item</DialogTitle></DialogHeader>
            <form onSubmit={saveEdit} className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                {[['Description','description','text',2],['Unit Price ($)','unit_price','number',1]].map(([label,key,type,cols]) => (
                  <div key={key} className={cols===2?'col-span-2':''}>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">{label}</label>
                    <input type={type} step={type==='number'?'0.01':undefined} value={editItem[key] || ''} onChange={e => setEditItem(p=>({...p,[key]:e.target.value}))}
                      className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                  </div>
                ))}
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Unit</label>
                  <select value={editItem.unit} onChange={e => setEditItem(p=>({...p,unit:e.target.value}))}
                    className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]">
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Notes</label>
                  <input type="text" value={editItem.notes || ''} onChange={e => setEditItem(p=>({...p,notes:e.target.value}))}
                    className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                </div>
              </div>
              <button type="submit" disabled={saving} className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] disabled:opacity-50">Save Changes</button>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Import Modal */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-2xl">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">Import Pricing</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-xs">
              Pick the format that matches what the vendor sent you.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {/* CSV / Excel — structured */}
              <div className="border-2 border-dashed border-[#2D3F5E] rounded-sm p-4 cursor-pointer hover:border-[#3B82F6]/50 transition-colors flex flex-col items-center justify-center gap-2 h-32"
                onClick={() => fileRef.current?.click()}
                data-testid="csv-upload-tile">
                <FileText size={22} className="text-[#3B82F6]" />
                <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-wide text-white font-bold">CSV / Excel</p>
                <p className="text-[10px] text-[#5C6A7A] text-center font-['IBM_Plex_Sans'] px-2">Structured spreadsheet with columns</p>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleImportFile} />
              </div>
              {/* AI Extract — PDF / image */}
              <div className="border-2 border-dashed border-[#9333EA]/40 rounded-sm p-4 cursor-pointer hover:border-[#9333EA]/70 transition-colors flex flex-col items-center justify-center gap-2 h-32 relative"
                onClick={() => !aiExtracting && aiFileRef.current?.click()}
                data-testid="ai-extract-tile"
                style={{ background: 'rgba(147, 51, 234, 0.06)' }}>
                {aiExtracting ? (
                  <>
                    <Loader2 size={22} className="text-[#9333EA] animate-spin" />
                    <p className="text-xs font-['JetBrains_Mono'] uppercase text-white font-bold">Extracting…</p>
                    <p className="text-[10px] text-[#5C6A7A] text-center font-['IBM_Plex_Sans']">~30 seconds</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5">
                      <Sparkles size={20} className="text-[#9333EA]" />
                      <span className="text-[9px] font-bold uppercase font-['JetBrains_Mono'] px-1.5 py-0.5 rounded-full bg-[#9333EA]/20 text-[#C084FC]">AI</span>
                    </div>
                    <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-wide text-white font-bold">PDF / Image</p>
                    <p className="text-[10px] text-[#5C6A7A] text-center font-['IBM_Plex_Sans'] px-2">AI reads vendor's price sheet PDF or photo</p>
                  </>
                )}
                <input ref={aiFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleAiExtractFile} />
              </div>
            </div>
            <p className="text-[10px] text-[#5C6A7A] font-['JetBrains_Mono'] uppercase tracking-widest">
              Expected: category · service_code · description · unit_price · unit · effective_from · notes
            </p>
            {importPreview && !importResult && (
              <div className="space-y-3">
                <p className="text-[#57EB31] text-xs font-['JetBrains_Mono']">
                  {importPreview.ai_extracted && <span className="text-[#C084FC]">✨ AI · </span>}
                  {importPreview.total_rows} rows · {importPreview.headers.length} columns
                  {importPreview.vendor_name_detected && (
                    <span className="ml-2 text-[#8B9AAB]">· detected vendor: {importPreview.vendor_name_detected}</span>
                  )}
                </p>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {['category','service_code','description','unit_price','unit','effective_from','notes'].map(field => (
                    <div key={field} className="flex items-center gap-2">
                      <span className="text-[10px] font-['JetBrains_Mono'] text-[#3B82F6] w-24 flex-shrink-0">{field}</span>
                      <select value={importMapping[field] || ''} onChange={e => setImportMapping(p=>({...p,[field]:e.target.value||undefined}))}
                        className="flex-1 h-7 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-[11px] focus:outline-none focus:border-[#3B82F6]">
                        <option value="">— skip —</option>
                        {importPreview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <button onClick={confirmImport} disabled={importing} className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-import-btn">
                  {importing ? <><Loader2 size={14} className="animate-spin" />Importing...</> : `Import ${importPreview.total_rows} rows`}
                </button>
              </div>
            )}
            {importResult && (
              <div className="p-4 bg-[#57EB31]/10 border border-[#57EB31]/30 rounded-sm">
                <p className="text-[#57EB31] font-['Manrope'] font-bold">✅ {importResult.created} price items imported</p>
                {importResult.errors?.length > 0 && <p className="text-amber-400 text-xs mt-1">{importResult.errors.length} rows skipped</p>}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ops Profile Modal */}
      <Dialog open={showOpsProfile} onOpenChange={setShowOpsProfile}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E]">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">Ops Profile — {vendor.name}</DialogTitle></DialogHeader>
          <form onSubmit={saveOpsProfile} className="space-y-4 mt-2">
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-2">Coverage States (where they travel/service)</label>
              <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                {US_STATES.map(s => (
                  <button key={s} type="button" onClick={() => setOpsForm(p => ({...p, coverage_states: p.coverage_states.includes(s) ? p.coverage_states.filter(x=>x!==s) : [...p.coverage_states, s]}))}
                    className={`px-2 py-0.5 text-[10px] rounded-sm border font-['JetBrains_Mono'] transition-all ${opsForm.coverage_states.includes(s) ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'border-[#2D3F5E] text-[#5C6A7A] hover:border-[#3B82F6]/40'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Ops Rating (1-5)</label>
                <input type="number" step="0.1" min="0" max="5" value={opsForm.ops_rating} onChange={e => setOpsForm(p=>({...p,ops_rating:e.target.value}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Typical ETA (hrs)</label>
                <input type="number" value={opsForm.eta_hours} onChange={e => setOpsForm(p=>({...p,eta_hours:e.target.value}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Travel Fee/Mile ($)</label>
                <input type="number" step="0.01" value={opsForm.travel_fee_per_mile} onChange={e => setOpsForm(p=>({...p,travel_fee_per_mile:e.target.value}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]" />
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Contract Notes / Discount Terms</label>
              <textarea value={opsForm.contract_notes} onChange={e => setOpsForm(p=>({...p,contract_notes:e.target.value}))} rows={3}
                placeholder="e.g. Net 30 payment, 5% discount on orders > $2,000..."
                className="w-full px-3 py-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] resize-none" />
            </div>
            <button type="submit" disabled={saving} className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] disabled:opacity-50">Save Ops Profile</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
